import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { PerformanceTeamMemberOverview } from 'app/library/performance-team-review/models/performance-team-member-overview.model';
import { TeamService } from 'app/library/team/services/team.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { UserToDosStatistics } from 'app/projects/user/src/lib/models/user-to-dos-statistics.model';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { takeUntil } from 'rxjs/operators';
import { TeamCertificationParentNameEnum } from '../../enums/team-certification-dialog-type.enum';
import { TeamBookingOverview } from '../../models/team-booking-overview.model';
import { Team } from '../../models/team.model';
import { TeamCertificationDialogComponent } from '../dialogs/team-certification-dialog/team-certification-dialog.component';

@Component({
    selector: 'app-user-progress',
    templateUrl: './user-progress.component.html',
    styleUrls: ['./user-progress.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class UserProgressComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    team: Team;

    @Input()
    memberId: string;

    @Input()
    performanceTeamMemberOverviews: PerformanceTeamMemberOverview[];

    @Input()
    approvalRequestsTeamOverview: { inProgressRequests: number };

    @Input()
    userToDoStatistics: UserToDosStatistics;

    @Input()
    bookingsTeamOverview: TeamBookingOverview;

    userId: string;
    isGradebookFeatureEnabled: boolean = false;
    isCertificateOfAttendanceFeatureEnabled: boolean = false;
    isCertificateMgmtFeatureEnabled: boolean = false;
    isToDoFeatureEnabled: boolean = false;

    private _hasTenantManagement: boolean = false;
    private _hasCertificationWrite: boolean = false;

    private get isCurrentUserTeamLead(): boolean {
        return !!this.team && (this.team.isCurrentUserLeader || this.team.isCurrentUserTeamLeaderSubteams);
    }

    private get isCurrentUserTrainingResponsible(): boolean {
        return !!this.team && (this.team.isCurrentUserTrainingResponsible || this.team.isCurrentUserUpperTrainingResponsible);
    }

    get canViewCertificates(): boolean {
        return this.isCurrentUserTeamLead || this.isCurrentUserTrainingResponsible || this._hasTenantManagement || this._hasCertificationWrite;
    }

    get canViewBookings(): boolean {
        return this.isCurrentUserTeamLead || this.isCurrentUserTrainingResponsible || this._hasTenantManagement;
    }

    get canViewApprovalRequests(): boolean {
        return this.isCurrentUserTeamLead || this._hasTenantManagement;
    }

    get canViewTodos(): boolean {
        return this.isCurrentUserTeamLead || this._hasTenantManagement;
    }

    constructor(public teamService: TeamService, private _matDialog: MatDialog, private _featureSwitchPipe: FeatureSwitchPipe, private _store: Store) {
        super();

        this.isGradebookFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Gradebook);
        this.isCertificateOfAttendanceFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CertificateOfAttendance);
        this.isCertificateMgmtFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.CertificateManagement);
        this.isToDoFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.ToDo);

        const permissions = this._store.selectSnapshot(UserState.getMyInfo).permissions;
        this._hasTenantManagement = permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
        this._hasCertificationWrite = permissions.includes(AdminPermissions.CERTIFICATION_WRITE);
    }

    ngOnInit(): void {
        this.teamService.userProgress$.pipe(takeUntil(this._unsubscribeAll)).subscribe((userProgress) => {
            this.userId = userProgress.user.id;
        });
    }

    openTeamCertificatesDialog(): void {
        const dialogRef = this._matDialog.open(TeamCertificationDialogComponent, {
            disableClose: false,
            width: '1244px',
        });

        dialogRef.componentInstance.title = 'PAGE.CERTIFICATIONS.TITLE';
        dialogRef.componentInstance.parentType = TeamCertificationParentNameEnum.TeamMember;
        dialogRef.componentInstance.userId = this.userId;
        dialogRef.componentInstance.team = this.team;
    }
}
